import { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import Modal from "../ui/Modal";
import ConfirmDialog from "../ui/ConfirmDialog";
import FormField, { FormActions, fieldInputStyle } from "../ui/FormField";
import {
  createFornecedor,
  updateFornecedor,
  deleteFornecedor,
  TIPO_FORNECEDOR_OPTIONS,
} from "../../services/fornecedoresService";

const EMPTY = {
  nome: "",
  tipo: 0,
  cidade: "",
  telefone: "",
  email: "",
  observacoes: "",
};

// Modal de criação/edição de fornecedor.
// Callback onCreated recebe o DTO criado — útil pro FinanceiroFormModal
// abrir este modal inline e pré-selecionar o recém-criado.
export default function FornecedorFormModal({
  open,
  fornecedor,
  onClose,
  onSaved,
  onDeleted,
  onCreated,
}) {
  const { C } = useTheme();
  const isEdit = !!fornecedor;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (fornecedor) {
      setForm({
        nome: fornecedor.nome ?? "",
        tipo: fornecedor.tipo ?? 0,
        cidade: fornecedor.cidade ?? "",
        telefone: fornecedor.telefone ?? "",
        email: fornecedor.email ?? "",
        observacoes: fornecedor.observacoes ?? "",
      });
    } else {
      setForm(EMPTY);
    }
    setError(null);
  }, [open, fornecedor]);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function close() {
    if (saving || deleting) return;
    onClose?.();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nome.trim()) {
      setError("O nome é obrigatório.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        nome: form.nome.trim(),
        tipo: Number(form.tipo),
        cidade: form.cidade.trim() || null,
        telefone: form.telefone.trim() || null,
        email: form.email.trim() || null,
        observacoes: form.observacoes.trim() || null,
      };
      if (isEdit) {
        await updateFornecedor(fornecedor.id, payload);
        onSaved?.();
      } else {
        const created = await createFornecedor(payload);
        onCreated?.(created);
        onSaved?.();
      }
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.join?.(" ") ||
          "Erro ao salvar fornecedor."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteFornecedor(fornecedor.id);
      setConfirmDelete(false);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao excluir fornecedor.");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  const inputStyle = fieldInputStyle(C);

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? "Editar Fornecedor" : "Novo Fornecedor"}
        subtitle={isEdit ? fornecedor?.nome : "Hotel, transporte, restaurante, etc."}
        width={560}
      >
        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            <FormField label="Nome" required span={2}>
              <input
                autoFocus
                value={form.nome}
                onChange={(e) => update("nome", e.target.value)}
                style={inputStyle}
                placeholder="Ex: Hotel Villa Serbelloni"
              />
            </FormField>

            <FormField label="Tipo" required span={2}>
              <select
                value={form.tipo}
                onChange={(e) => update("tipo", Number(e.target.value))}
                style={inputStyle}
              >
                {TIPO_FORNECEDOR_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.icon} {t.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Cidade">
              <input
                value={form.cidade}
                onChange={(e) => update("cidade", e.target.value)}
                style={inputStyle}
                placeholder="Bellagio"
              />
            </FormField>

            <FormField label="Telefone">
              <input
                value={form.telefone}
                onChange={(e) => update("telefone", e.target.value)}
                style={inputStyle}
                placeholder="+39 031..."
              />
            </FormField>

            <FormField label="E-mail" span={2}>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                style={inputStyle}
                placeholder="contato@fornecedor.it"
              />
            </FormField>

            <FormField label="Observações" span={2}>
              <textarea
                value={form.observacoes}
                onChange={(e) => update("observacoes", e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
                placeholder="Contato do gerente, código corporativo, condições de pagamento..."
              />
            </FormField>
          </div>

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.redDim,
                color: C.red,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}

          <FormActions
            onCancel={close}
            onDelete={isEdit ? () => setConfirmDelete(true) : undefined}
            saving={saving}
            submitLabel={isEdit ? "Salvar alterações" : "Criar fornecedor"}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir fornecedor?"
        message={`Tem certeza que deseja excluir "${fornecedor?.nome}"? Despesas já lançadas manterão o registro histórico, mas perderão o vínculo com este fornecedor.`}
        confirmLabel="Excluir"
        danger
        loading={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
